/**
 * Portal View Model Builder
 *
 * Pure function that transforms engine output + collected data into
 * the PortalViewModel consumed by the portal UI.
 *
 * No side effects. No React. Fully testable.
 */

import type { SystemRecOutput, RecommendationOption, RedFlag, ConfidenceSummary } from '@core/types';
import type { CollectedData } from '../../context/RecommendationContext';
import type { NodeFieldNode } from '@system-rec/decision-ui';
import { getValue, getCertainty, getSource, getEvidence, isConfirmed, isAssumed, isUnknown } from '../../utils/provenanceHelpers';

// ─── Types ───────────────────────────────────────────────────────

export interface PortalViewModel {
  header: PortalHeader;
  decisionAxis: DecisionAxisData;
  swot: DualSwotData;
  evidence: EvidenceItem[];
  heating: HeatingAssessment;
  waterServices: WaterServicesAssessment;
}

export interface PortalHeader {
  propertySummary: string;
  confidenceLevel: 'high' | 'medium' | 'low';
  confidenceLabel: string;
}

export interface DecisionAxisData {
  optionA: OptionDetail;
  optionAPlus: OptionDetail;
  optionB: OptionDetail | null;
}

export interface OptionDetail {
  title: string;
  subtitle: string;
  included: InterventionItem[];
  risksRemaining: RiskItem[];
  problemsSolved: ResolvedItem[];
}

export interface InterventionItem {
  id: string;
  label: string;
  explanation: string;
  optionLevel: 'a' | 'a+' | 'both';
  category: 'heating' | 'hot_water' | 'controls' | 'system_health';
}

export interface RiskItem {
  id: string;
  label: string;
  detail: string;
  swotNodeId?: string;
}

export interface ResolvedItem {
  id: string;
  label: string;
  resolution: 'resolved' | 'mitigated';
}

export interface DualSwotData {
  statusQuo: SwotQuadrants;
  recommended: SwotQuadrants;
}

export interface SwotQuadrants {
  strengths: SwotItem[];
  weaknesses: SwotItem[];
  opportunities: SwotItem[];
  threats: SwotItem[];
}

export interface SwotItem {
  id: string;
  label: string;
  icon?: string;
  detail?: string;
  resolvedBy?: 'a' | 'a+' | null;
  evidenceBasis?: 'measured' | 'assumed' | 'inferred';
}

export interface EvidenceItem {
  id: string;
  label: string;
  status: 'confirmed' | 'assumed' | 'unknown';
  value?: string;
  reason?: string;
  ifWrong?: string;
}

export interface HeatingAssessment {
  heatSourceType: string;
  distributionConstraints: string[];
  flowTemperature: {
    label: string;
    constraint?: string;
  };
  efficiencyContext: string;
  heatLoss?: {
    current: number;
    target: number;
  };
  /** Quantified heat loss summary when simple model data is available */
  heatLossSummary?: {
    totalHeatLossKW: number;
    sizingTargetKW: number;
    designTempExternalC: number;
    confidence0to100: number;
    confidenceBand: string;
    notes: string[];
  };
  heatPumpViability?: {
    viable: boolean;
    constraints: string[];
    enablers: string[];
  };
}

export interface WaterServicesAssessment {
  strategy: string;
  reasoning: string;
  mainsCapacity: {
    label: string;
    adequate: boolean;
  };
  peakDemandFit: string;
}

// ─── Helpers ─────────────────────────────────────────────────────

const PROPERTY_LABELS: Record<string, string> = {
  detached: 'Detached house',
  'semi-detached': 'Semi-detached house',
  semi: 'Semi-detached house',
  terrace: 'Terraced house',
  terraced: 'Terraced house',
  flat: 'Flat',
  bungalow: 'Bungalow',
};

const AGE_LABELS: Record<string, string> = {
  'pre-1930': 'pre-1930',
  'pre-1919': 'pre-1919',
  '1930-1979': '1930–1979',
  '1950-1975': '1950–1975',
  '1976-1990': '1976–1990',
  '1980-2000': '1980–2000',
  '1991-2006': '1991–2006',
  '2001-2010': '2001–2010',
  '2007+': '2007+',
  '2011+': '2011+',
};

const SYSTEM_LABELS: Record<string, string> = {
  combi: 'Combi boiler',
  system: 'System boiler',
  regular: 'Regular (conventional) boiler',
  heat_pump: 'Air source heat pump',
  ashp: 'Air source heat pump',
  hybrid: 'Hybrid system',
};

const WATER_LABELS: Record<string, string> = {
  combi_instant: 'Combi (on-demand with lag)',
  instant: 'Combi (on-demand with lag)',
  on_demand: 'On-demand with lag',
  vented_cylinder: 'Vented cylinder',
  unvented_cylinder: 'Unvented cylinder',
  thermal_store: 'Thermal store',
  open_vented: 'Open vented cylinder',
};

function getPropertyLabel(type: string | undefined): string {
  if (!type) return 'Property';
  return PROPERTY_LABELS[type] || type;
}

function getAgeBandLabel(band: string | undefined): string {
  if (!band) return '';
  return AGE_LABELS[band] || band;
}

function getSystemLabel(type: string | undefined): string {
  if (!type) return 'heating system';
  return SYSTEM_LABELS[type] || type;
}


function simplifyControlDescription(text: string): string {
  return text
    .replace(/load[- ]?modulating protocol/gi, 'load compensation')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

function getWaterLabel(type: string | undefined): string {
  if (!type) return 'hot water system';
  return WATER_LABELS[type] || type;
}

// ─── Builder ─────────────────────────────────────────────────────

export interface OptionSlots {
  optionAId?: string;
  optionBId?: string;
  premiumScope?: string[];
}

export function buildPortalViewModel(
  output: SystemRecOutput,
  data: CollectedData | null,
  swotNodes?: { current: NodeFieldNode[]; proposed: NodeFieldNode[] },
  optionSlots?: OptionSlots,
): PortalViewModel {
  const primary = output.primary;
  const input = output.inputEcho;
  const alternativeOption = output.alternatives?.[0] ?? null;

  return {
    header: buildHeader(output, data),
    decisionAxis: buildDecisionAxis(primary, output, data, alternativeOption, optionSlots),
    swot: buildSwotData(swotNodes, output),
    evidence: buildEvidence(output, data),
    heating: buildHeatingAssessment(primary, output, data),
    waterServices: buildWaterServices(primary, output, data),
  };
}

// ─── Header ──────────────────────────────────────────────────────

function buildHeader(output: SystemRecOutput, data: CollectedData | null): PortalHeader {
  const input = output.inputEcho;
  const propType = getPropertyLabel(data?.propertyType || input?.propertyType);
  const ageBand = getAgeBandLabel(data?.propertyAgeBand || input?.propertyAgeBand);
  const bedrooms = data?.bedrooms || input?.bedrooms || '—';
  const bathrooms = data?.bathrooms || input?.bathrooms || '1';

  const parts = [propType];
  if (ageBand) parts.push(ageBand);
  parts.push(`${bedrooms} bed`);
  parts.push(`${bathrooms} bath`);
  const propertySummary = parts.join(' · ');

  const conf = output.confidenceSummary;
  const level = conf?.level || 'medium';
  const labelMap: Record<string, string> = {
    high: 'High confidence — based on measurements',
    medium: 'Medium confidence — some assumptions used',
    low: 'Low confidence — significant unknowns remain',
  };

  return {
    propertySummary,
    confidenceLevel: level,
    confidenceLabel: labelMap[level] || labelMap.medium,
  };
}

// ─── Decision Axis (A vs A+) ────────────────────────────────────

function buildDecisionAxis(
  primary: RecommendationOption,
  _output: SystemRecOutput,
  data: CollectedData | null,
  alternativeOption: RecommendationOption | null,
  _optionSlots?: OptionSlots,
): DecisionAxisData {
  // Option A: the essential system change
  const aIncluded: InterventionItem[] = [];
  const aPlusIncluded: InterventionItem[] = [];

  // Core system — always in both
  aIncluded.push({
    id: 'system-change',
    label: `${primary.boilerLabel} with ${primary.waterLabel}`,
    explanation: primary.why || primary.summary,
    optionLevel: 'both',
    category: 'heating',
  });

  // Derive A vs A+ from red flags and cons
  const sludgeRisk = data?.sludgeRisk;
  const hasFilter = data?.hasMagneticFilter === 'yes';
  const pipeBore = data?.pipeBore;
  const controlsPref = data?.currentThermostatType;

  // System flush
  if (sludgeRisk === 'confirmed' || sludgeRisk === 'likely') {
    aIncluded.push({
      id: 'powerflush',
      label: 'System powerflush',
      explanation: 'Magnetite sludge has been identified. A powerflush removes contamination before the new system is installed.',
      optionLevel: 'a',
      category: 'system_health',
    });
  } else if (sludgeRisk === 'possible') {
    aPlusIncluded.push({
      id: 'powerflush',
      label: 'System powerflush',
      explanation: 'Sludge contamination is possible. A powerflush ensures clean water flow for the new system.',
      optionLevel: 'a+',
      category: 'system_health',
    });
  }

  // Magnetic filter
  if (!hasFilter) {
    if (sludgeRisk === 'confirmed' || sludgeRisk === 'likely') {
      aIncluded.push({
        id: 'filter',
        label: 'Magnetic filter',
        explanation: 'Protects the new heat exchanger from future sludge accumulation.',
        optionLevel: 'a',
        category: 'system_health',
      });
    } else {
      aPlusIncluded.push({
        id: 'filter',
        label: 'Magnetic filter',
        explanation: 'Protects the heat exchanger from magnetite. Extends system life and maintains efficiency.',
        optionLevel: 'a+',
        category: 'system_health',
      });
    }
  }

  // Controls upgrade
  if (controlsPref === 'mechanical' || controlsPref === 'basic_digital') {
    aPlusIncluded.push({
      id: 'controls',
      label: 'Smart controls with load compensation',
      explanation: 'Modulates boiler output to match actual demand. Reduces cycling, improves comfort, lowers running costs.',
      optionLevel: 'a+',
      category: 'controls',
    });
  }

  // Thermostat recommendation from engine
  if (primary.thermostat?.recommendedType === 'smart_load_comp') {
    if (!aPlusIncluded.find(i => i.id === 'controls')) {
      aPlusIncluded.push({
        id: 'controls',
        label: 'Smart controls with load compensation',
        explanation: simplifyControlDescription(primary.thermostat.reasoning || 'Optimises boiler modulation for efficiency.'),
        optionLevel: 'a+',
        category: 'controls',
      });
    }
  }

  // Pipework
  if (pipeBore === '8mm' || pipeBore === '10mm' || pipeBore === '15mm') {
    const isCritical = pipeBore === '8mm' || pipeBore === '10mm';
    if (isCritical) {
      aIncluded.push({
        id: 'pipework',
        label: 'Pipework review',
        explanation: `${pipeBore} microbore pipework restricts flow. Must be assessed before installation.`,
        optionLevel: 'a',
        category: 'system_health',
      });
    } else {
      aPlusIncluded.push({
        id: 'pipework',
        label: 'Pipework assessment',
        explanation: '15mm pipework may limit performance for some system types. Assessment recommended.',
        optionLevel: 'a+',
        category: 'system_health',
      });
    }
  }

  // Risks remaining in A
  const aRisks: RiskItem[] = [];
  primary.cons.forEach((con, i) => {
    aRisks.push({
      id: `con-${i}`,
      label: con,
      detail: con,
    });
  });

  // A+ resolves some of these
  const aPlusRisks = aRisks.filter(r =>
    !aPlusIncluded.some(item =>
      r.label.toLowerCase().includes('control') && item.id === 'controls' ||
      r.label.toLowerCase().includes('sludge') && item.id === 'powerflush' ||
      r.label.toLowerCase().includes('filter') && item.id === 'filter'
    )
  );

  // Problems solved by A
  const aSolved: ResolvedItem[] = primary.pros.map((pro, i) => ({
    id: `pro-${i}`,
    label: pro,
    resolution: 'resolved' as const,
  }));

  // A+ solves additional
  const aPlusSolved: ResolvedItem[] = [
    ...aSolved,
    ...aPlusIncluded.map(item => ({
      id: `aplus-${item.id}`,
      label: item.label,
      resolution: 'resolved' as const,
    })),
  ];

  // Build Option B from alternative option (if available)
  let optionB: OptionDetail | null = null;
  if (alternativeOption) {
    const bIncluded: InterventionItem[] = [
      {
        id: 'alt-system-change',
        label: `${alternativeOption.boilerLabel} with ${alternativeOption.waterLabel}`,
        explanation: alternativeOption.why || alternativeOption.summary,
        optionLevel: 'both',
        category: 'heating',
      },
    ];

    const bRisks: RiskItem[] = alternativeOption.cons.map((con, i) => ({
      id: `alt-con-${i}`,
      label: con,
      detail: con,
    }));

    const bSolved: ResolvedItem[] = alternativeOption.pros.map((pro, i) => ({
      id: `alt-pro-${i}`,
      label: pro,
      resolution: 'resolved' as const,
    }));

    optionB = {
      title: 'Alternative Path',
      subtitle: alternativeOption.why || 'A different approach that may suit different priorities.',
      included: bIncluded,
      risksRemaining: bRisks,
      problemsSolved: bSolved,
    };
  }

  return {
    optionA: {
      title: 'Essential',
      subtitle: 'The minimum sensible solution that will work safely and reliably.',
      included: aIncluded,
      risksRemaining: aRisks,
      problemsSolved: aSolved,
    },
    optionAPlus: {
      title: 'Performance & Longevity',
      subtitle: 'The same solution, with changes that improve efficiency, lifespan, and predictability.',
      included: [...aIncluded, ...aPlusIncluded],
      risksRemaining: aPlusRisks,
      problemsSolved: aPlusSolved,
    },
    optionB,
  };
}

// ─── SWOT ────────────────────────────────────────────────────────

function nodeToSwotItem(node: NodeFieldNode): SwotItem {
  return {
    id: node.id,
    label: node.label,
    icon: node.icon,
    detail: node.description,
  };
}

function partitionByQuadrant(nodes: NodeFieldNode[]): SwotQuadrants {
  return {
    strengths: nodes.filter(n => n.quadrant === 'strengths').map(nodeToSwotItem),
    weaknesses: nodes.filter(n => n.quadrant === 'weaknesses').map(nodeToSwotItem),
    opportunities: nodes.filter(n => n.quadrant === 'opportunities').map(nodeToSwotItem),
    threats: nodes.filter(n => n.quadrant === 'threats').map(nodeToSwotItem),
  };
}

function buildSwotData(
  swotNodes: { current: NodeFieldNode[]; proposed: NodeFieldNode[] } | undefined,
  output: SystemRecOutput,
): DualSwotData {
  if (swotNodes) {
    return {
      statusQuo: partitionByQuadrant(swotNodes.current),
      recommended: partitionByQuadrant(swotNodes.proposed),
    };
  }

  // Fallback: build minimal SWOT from engine output
  const statusQuo: SwotQuadrants = {
    strengths: [],
    weaknesses: [],
    opportunities: [],
    threats: [],
  };
  const recommended: SwotQuadrants = {
    strengths: [],
    weaknesses: [],
    opportunities: [],
    threats: [],
  };

  // Red flags → status quo threats/weaknesses
  output.redFlags?.forEach(flag => {
    const target = flag.severity === 'warning' ? statusQuo.threats : statusQuo.weaknesses;
    target.push({
      id: flag.id,
      label: flag.title,
      detail: flag.detail,
    });
    // If mitigated, add to recommended strengths
    if (flag.mitigation) {
      recommended.strengths.push({
        id: `resolved-${flag.id}`,
        label: flag.mitigation,
        detail: `Addresses: ${flag.title}`,
      });
    }
  });

  // Primary pros → recommended strengths
  output.primary.pros.forEach((pro, i) => {
    recommended.strengths.push({ id: `pro-${i}`, label: pro });
  });

  // Primary cons → recommended weaknesses (residual)
  output.primary.cons.forEach((con, i) => {
    recommended.weaknesses.push({ id: `con-${i}`, label: con });
  });

  // Assumptions → status quo weaknesses
  output.assumptions.slice(0, 3).forEach((assumption, i) => {
    statusQuo.weaknesses.push({
      id: `assumption-${i}`,
      label: assumption,
      evidenceBasis: 'assumed',
    });
  });

  // If a weakness is present in both status quo and recommended systems,
  // it is not decision-relevant and should cancel out in the presentation.
  const normalizeWeakness = (value: string) =>
    value.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  const currentWeaknessSet = new Set(statusQuo.weaknesses.map((w) => normalizeWeakness(w.label)));
  const recommendedWeaknessSet = new Set(recommended.weaknesses.map((w) => normalizeWeakness(w.label)));
  const sharedWeaknesses = new Set(
    [...currentWeaknessSet].filter((label) => recommendedWeaknessSet.has(label)),
  );

  if (sharedWeaknesses.size > 0) {
    statusQuo.weaknesses = statusQuo.weaknesses.filter(
      (w) => !sharedWeaknesses.has(normalizeWeakness(w.label)),
    );
    recommended.weaknesses = recommended.weaknesses.filter(
      (w) => !sharedWeaknesses.has(normalizeWeakness(w.label)),
    );
  }

  return { statusQuo, recommended };
}

// ─── Evidence ────────────────────────────────────────────────────

function buildEvidence(
  output: SystemRecOutput,
  data: CollectedData | null,
): EvidenceItem[] {
  const items: EvidenceItem[] = [];
  const input = output.inputEcho;

  // Helper to build evidence item from a field with provenance support
  const buildItem = (
    id: string,
    label: string,
    field: any,
    valueFormatter?: (val: string) => string,
    assumedReason?: string,
    ifWrongText?: string
  ): EvidenceItem | null => {
    const value = getValue(field);
    const certainty = getCertainty(field);
    const evidence = getEvidence(field);
    
    // Skip empty/unknown fields unless they're genuinely unknown (not just unset)
    if (!value || value === 'unknown') {
      if (certainty === 'unknown') {
        return {
          id,
          label,
          status: 'unknown',
          reason: assumedReason || 'Not specified',
          ifWrong: ifWrongText,
        };
      }
      return null;
    }

    const formattedValue = valueFormatter ? valueFormatter(String(value)) : String(value);
    
    return {
      id,
      label,
      status: certainty,
      value: formattedValue,
      reason: certainty === 'assumed' ? (evidence || assumedReason) : undefined,
      ifWrong: certainty !== 'confirmed' ? ifWrongText : undefined,
    };
  };

  // Property data
  const propertyTypeItem = buildItem(
    'property-type',
    'Property type',
    data?.propertyType || input?.propertyType,
    getPropertyLabel
  );
  if (propertyTypeItem) items.push(propertyTypeItem);

  const ageItem = buildItem(
    'age-band',
    'Property age',
    data?.propertyAgeBand || input?.propertyAgeBand,
    getAgeBandLabel
  );
  if (ageItem) items.push(ageItem);

  // Efficiency source - check both data and input, with proper source detection
  const efficiencySource = input?.currentSystemEfficiency?.source;
  
  // Extract values from data (which may have FieldProvenance wrappers)
  const gcNumberDataValue = data?.gcNumber ? getValue(data.gcNumber) : null;
  const sedbukBandDataValue = data?.sedbukBand ? getValue(data.sedbukBand) : null;
  const epcBandDataValue = data?.epcBand ? getValue(data.epcBand) : null;
  
  // Check data fields (with FieldProvenance extraction)
  const hasGcNumberData = !!(gcNumberDataValue && String(gcNumberDataValue).trim() !== 'unknown');
  const hasSedbukBandData = !!(sedbukBandDataValue && sedbukBandDataValue !== 'unknown');
  const hasEpcBandData = !!(epcBandDataValue && epcBandDataValue !== 'unknown');
  
  // Check input fields (plain values, no FieldProvenance)
  const hasGcNumberInput = !!(input?.currentSystemEfficiency?.gcNumber && input.currentSystemEfficiency.gcNumber.trim() !== 'unknown');
  const hasSedbukBandInput = !!(input?.currentSystemEfficiency?.sedbukBand && input.currentSystemEfficiency.sedbukBand !== 'unknown');
  const hasEpcBandInput = !!(input?.currentSystemEfficiency?.epcBand && input.currentSystemEfficiency.epcBand !== 'unknown');
  
  const hasGcNumber = hasGcNumberData || hasGcNumberInput;
  const hasSedbukBand = hasSedbukBandData || hasSedbukBandInput;
  const hasEpcBand = hasEpcBandData || hasEpcBandInput;
  
  if (hasGcNumber || hasSedbukBand || hasEpcBand) {
    // Efficiency data is present - determine if it's collected or assumed based on source
    const isCollected = efficiencySource && efficiencySource !== 'ASSUMED';
    items.push({
      id: 'efficiency',
      label: 'Boiler efficiency',
      status: isCollected ? 'confirmed' : 'assumed',
      value: hasGcNumber
        ? `SEDBUK lookup (GC: ${gcNumberDataValue || input?.currentSystemEfficiency?.gcNumber})`
        : hasSedbukBand
          ? `SEDBUK Band ${sedbukBandDataValue || input?.currentSystemEfficiency?.sedbukBand}`
          : `ErP / EPC band ${epcBandDataValue || input?.currentSystemEfficiency?.epcBand}`,
      reason: isCollected ? undefined : 'Typical for system type and age',
      ifWrong: 'Running cost projections may differ',
    });
  } else {
    items.push({
      id: 'efficiency',
      label: 'Boiler efficiency',
      status: 'assumed',
      reason: 'Typical for system type and age',
      ifWrong: 'Running cost projections may differ',
    });
  }

  // Current boiler output
  const boilerKwItem = buildItem(
    'boiler-output-kw',
    'Current boiler output',
    data?.currentBoilerKw ?? input?.currentSystemEfficiency?.boilerOutputKw,
    (val: string) => {
      const normalized = String(val).trim().toLowerCase();
      if (normalized === '<8kw') {
        return '<8kW';
      }
      const numeric = Number.parseFloat(normalized.replace('kw', ''));
      if (Number.isFinite(numeric)) {
        return `${numeric} kW`;
      }
      return val;
    }
  );
  if (boilerKwItem) items.push(boilerKwItem);

  // Mains pressure
  const mainsPressureLabelMap: Record<string, string> = {
    low: 'Low mains pressure',
    adequate: 'Adequate mains pressure',
    high: 'High mains pressure',
  };
  
  const mainsPressureValue = input?.mainsPressure || input?.mainsStaticPressureBar || data?.mainsPressure;
  if (mainsPressureValue && typeof mainsPressureValue === 'number') {
    items.push({
      id: 'mains-pressure',
      label: 'Mains pressure',
      status: data?.mainsPressure ? getCertainty(data.mainsPressure) : 'confirmed',
      value: `${mainsPressureValue} bar`,
    });
  } else if (mainsPressureValue && mainsPressureValue !== 'not_tested' && mainsPressureValue !== 'unknown') {
    items.push({
      id: 'mains-pressure',
      label: 'Mains pressure',
      status: data?.mainsPressure ? getCertainty(data.mainsPressure) : 'confirmed',
      value: mainsPressureLabelMap[String(mainsPressureValue)] || String(mainsPressureValue),
    });
  } else {
    items.push({
      id: 'mains-pressure',
      label: 'Mains pressure',
      status: 'unknown',
      reason: 'Not measured on site',
      ifWrong: 'Hot water strategy may need reassessing',
    });
  }

  // Controls and zoning/TRV context
  const thermostatItem = buildItem(
    'controls-type',
    'Current thermostat control',
    data?.currentThermostatType || input?.currentThermostatType,
    (val: string) => {
      const thermostatLabelMap: Record<string, string> = {
        mechanical: 'Mechanical thermostat',
        basic_digital: 'Digital programmer thermostat',
        smart_on_off: 'Smart thermostat',
        smart_load_comp: 'Load-compensating smart thermostat',
      };
      return thermostatLabelMap[val] || val;
    }
  );
  if (thermostatItem) items.push(thermostatItem);

  if (data?.wantMultiZone) {
    items.push({
      id: 'trv-zoning',
      label: 'TRVs / multi-room zoning',
      status: getCertainty(data.wantMultiZone),
      value: getValue(data.wantMultiZone) === 'yes' ? 'Requested / present' : 'Not requested',
    });
  }

  // Emitter sizing
  const emitterType = data?.emitterType || input?.heatEmitterType;
  const emitterItem = buildItem(
    'emitter-type',
    'Emitter type',
    emitterType,
    (val: string) => {
      if (val === 'standard_rads') return 'Standard radiators';
      if (val === 'oversized_rads') return 'Oversized radiators';
      if (val === 'underfloor_heating') return 'Underfloor heating';
      if (val === 'radiators') return 'Radiators';
      if (val === 'ufh') return 'Underfloor heating';
      if (val === 'mixed') return 'Mixed (radiators + UFH)';
      return val;
    },
    'Estimated from property type and age',
    'Low-temperature suitability assessment may change'
  );
  if (emitterItem) items.push(emitterItem);

  // Pipework
  const pipework = data?.pipeBore || input?.emitters?.pipeBore;
  const pipeworkItem = buildItem(
    'pipework',
    'Primary pipework',
    pipework,
    undefined,
    'Not measured on site',
    'Flow rates and heat pump viability may be affected'
  );
  if (pipeworkItem) {
    items.push(pipeworkItem);
  } else {
    items.push({
      id: 'pipework',
      label: 'Primary pipework size',
      status: 'unknown',
      ifWrong: 'Flow rates and heat pump viability may be affected',
    });
  }

  // Sludge
  const sludge = data?.sludgeRisk;
  if (sludge === 'confirmed') {
    items.push({
      id: 'sludge',
      label: 'System contamination',
      status: 'confirmed',
      value: 'Confirmed sludge present',
    });
  } else if (sludge === 'likely' || sludge === 'possible') {
    items.push({
      id: 'sludge',
      label: 'System contamination',
      status: 'assumed',
      value: sludge === 'likely' ? 'Likely sludge' : 'Possible sludge',
      reason: 'Based on system age and symptoms',
      ifWrong: 'Powerflush may not be needed',
    });
  }

  // Insulation - use provenance-aware logic
  const insulation = data?.insulationLevel || input?.insulationLevel;
  const insulationItem = buildItem(
    'insulation',
    'Insulation level',
    insulation,
    (val: string) => val.charAt(0).toUpperCase() + val.slice(1),
    'Estimated from property type and age',
    'Heat loss calculations and system sizing may change'
  );
  if (insulationItem) {
    items.push(insulationItem);
  } else {
    items.push({
      id: 'insulation',
      label: 'Insulation level',
      status: 'unknown',
      ifWrong: 'Heat loss estimate is less reliable',
    });
  }

  // Hot water demand - check both data and input sources
  const occupancy = data?.occupancyCount || (typeof input?.occupancy === 'number' ? String(input.occupancy) : undefined);
  const demandLabelMap: Record<string, string> = {
    low: 'Low',
    medium: 'Medium',
    high: 'High',
  };
  
  const hwDemand = data?.hotWaterDemand || input?.hotWaterDemand;
  if (hwDemand && hwDemand !== 'unknown') {
    const demandValue = getValue(hwDemand);
    const demandCertainty = getCertainty(hwDemand);
    const demandEvidence = getEvidence(hwDemand);
    items.push({
      id: 'hw-demand',
      label: 'Hot water demand',
      status: demandCertainty,
      value: demandLabelMap[String(demandValue)] || String(demandValue),
      reason: demandCertainty === 'assumed' ? demandEvidence : undefined,
      ifWrong: 'Peak demand and cylinder sizing may need adjusting',
    });
  } else if (occupancy) {
    // Inferred from occupancy
    items.push({
      id: 'hw-demand',
      label: 'Hot water demand',
      status: 'assumed',
      value: `Inferred from ${occupancy} occupants`,
      reason: 'Based on household size and bathroom count',
      ifWrong: 'Peak demand and cylinder sizing may need adjusting',
    });
  } else {
    items.push({
      id: 'hw-demand',
      label: 'Hot water demand',
      status: 'unknown',
      reason: 'Household demand profile not yet captured',
      ifWrong: 'Peak demand and cylinder sizing may need adjusting',
    });
  }

  // Add engine assumptions
  output.assumptions.forEach((a, i) => {
    const normalizedAssumption = a.toLowerCase();
    const hasKnownBoilerOutput = items.some(
      item => item.id === 'boiler-output-kw' && item.status !== 'unknown'
    );
    const hasEmitterEvidence = items.some(
      item => (item.id === 'emitter-type' || item.id === 'pipework') && item.status !== 'unknown'
    );

    // Never show generic/non-actionable assumptions in the assumptions list.
    if (normalizedAssumption.includes('these assumptions are common')) {
      return;
    }

    // Gas/LPG availability is not a useful assumption to display in this evidence panel.
    if (normalizedAssumption.includes('gas') && (normalizedAssumption.includes('available') || normalizedAssumption.includes('lpg'))) {
      return;
    }

    // Suppress assumptions that are already covered by captured evidence.
    if (hasKnownBoilerOutput && normalizedAssumption.includes('boiler output')) {
      return;
    }
    if (hasEmitterEvidence && normalizedAssumption.includes('radiator') && normalizedAssumption.includes('suitability')) {
      return;
    }

    const hasComparableItem = items.some(item => {
      const label = item.label.toLowerCase();
      return label === normalizedAssumption || normalizedAssumption.includes(label) || label.includes(normalizedAssumption);
    });

    if (!hasComparableItem) {
      items.push({
        id: `engine-assumption-${i}`,
        label: a,
        status: 'assumed',
        reason: 'Engine default',
      });
    }
  });

  return items;
}

// ─── Heating Assessment ──────────────────────────────────────────

function buildHeatingAssessment(
  primary: RecommendationOption,
  output: SystemRecOutput,
  data: CollectedData | null,
): HeatingAssessment {
  const input = output.inputEcho;
  const isHeatPump = primary.systemType === 'heat_pump' || primary.systemType === 'ashp';
  const isHybrid = primary.systemType === 'hybrid';

  const constraints: string[] = [];
  const metrics = output.derivedMetrics;

  if (metrics?.emitterProfile?.pipeBore === '8mm' || metrics?.emitterProfile?.pipeBore === '10mm') {
    constraints.push(`${metrics.emitterProfile.pipeBore} microbore pipework restricts flow rates`);
  }
  if (metrics?.emitterProfile?.lowTempSuitability === 'definitely_needs_upgrade') {
    constraints.push('Emitters require upgrade for low-temperature operation');
  }
  if (metrics?.emitterProfile?.roomsStruggleToHeat === 'yes') {
    constraints.push('Some rooms currently struggle to reach temperature');
  }

  let flowTempLabel = '55–80°C (standard gas boiler)';
  let flowTempConstraint: string | undefined;

  if (isHeatPump) {
    flowTempLabel = '35–55°C (heat pump operating range)';
    if (metrics?.lowTempReadiness === 'poor') {
      flowTempConstraint = 'Current emitters may not deliver adequate heat at low flow temperatures';
    }
  }

  let efficiencyContext = primary.summary || `${getSystemLabel(primary.systemType)} — standard efficiency for this system type.`;

  // Heat loss benchmarks (W/m²)
  const HEAT_LOSS_POOR = 140;      // Poor insulation, typical pre-1919
  const HEAT_LOSS_AVERAGE = 100;   // Average insulation
  const HEAT_LOSS_GOOD = 70;       // Good insulation
  const HEAT_LOSS_VERY_POOR = 150; // Very poor, old building, no upgrades
  const HEAT_LOSS_TARGET = 60;     // Target for good energy efficiency

  // Derive heat loss estimates from property data
  let heatLoss: { current: number; target: number } | undefined;
  const insulationLevel = data?.insulationLevel || input?.insulationLevel;
  const propertyAge = data?.propertyAgeBand || input?.propertyAgeBand;
  
  // Estimate heat loss based on insulation and property age
  if (insulationLevel || propertyAge) {
    let estimatedHeatLoss = HEAT_LOSS_AVERAGE; // Default average
    
    // Adjust based on insulation
    if (insulationLevel === 'poor') {
      estimatedHeatLoss = HEAT_LOSS_POOR;
    } else if (insulationLevel === 'average') {
      estimatedHeatLoss = HEAT_LOSS_AVERAGE;
    } else if (insulationLevel === 'good') {
      estimatedHeatLoss = HEAT_LOSS_GOOD;
    }
    
    // Adjust based on property age (older = worse unless retrofitted)
    // Use Math.max to ensure we don't improve the estimate for old properties
    // when no retrofit data is available (conservative approach)
    if (propertyAge === 'pre-1919' || propertyAge === 'pre-1930') {
      if (!insulationLevel || insulationLevel === 'poor') {
        estimatedHeatLoss = Math.max(estimatedHeatLoss, HEAT_LOSS_VERY_POOR);
      }
    }
    
    heatLoss = {
      current: estimatedHeatLoss,
      target: HEAT_LOSS_TARGET,
    };
  }

  let heatPumpViability: HeatingAssessment['heatPumpViability'] | undefined;
  if (data?.considerHeatPump === 'yes' || isHeatPump || isHybrid) {
    const hpConstraints: string[] = [];
    const hpEnablers: string[] = [];

    if (metrics?.lowTempReadiness === 'poor') {
      hpConstraints.push('Emitters not sized for low-temperature operation');
      hpEnablers.push('Replace undersized radiators or add underfloor heating');
    }
    if (metrics?.emitterProfile?.pipeBore === '8mm' || metrics?.emitterProfile?.pipeBore === '10mm') {
      hpConstraints.push('Microbore pipework limits flow');
      hpEnablers.push('Upgrade primary pipework to 22mm+');
    }
    if (data?.outdoorSpace === 'none') {
      hpConstraints.push('No outdoor space for external unit');
    }
    if (metrics?.retrofitLevel === 'poor') {
      hpConstraints.push('Poor fabric insulation increases required output');
      hpEnablers.push('Improve wall and loft insulation');
    }

    heatPumpViability = {
      viable: hpConstraints.length <= 1,
      constraints: hpConstraints,
      enablers: hpEnablers,
    };
  }

  return {
    heatSourceType: primary.boilerLabel || getSystemLabel(primary.systemType),
    distributionConstraints: constraints,
    flowTemperature: { label: flowTempLabel, constraint: flowTempConstraint },
    efficiencyContext,
    heatLoss,
    heatPumpViability,
    ...(output.simpleHeatLossResult ? {
      heatLossSummary: {
        totalHeatLossKW: output.simpleHeatLossResult.totalHeatLossKW,
        sizingTargetKW: output.simpleHeatLossResult.sizingTargetKW,
        designTempExternalC: output.simpleHeatLossResult.designTempExternalC,
        confidence0to100: output.simpleHeatLossResult.confidence0to100,
        confidenceBand: output.simpleHeatLossResult.confidence0to100 >= 80 ? 'High'
          : output.simpleHeatLossResult.confidence0to100 >= 60 ? 'Medium'
          : 'Low',
        notes: output.simpleHeatLossResult.notes,
      },
    } : {}),
  };
}

// ─── Water Services ──────────────────────────────────────────────

function buildWaterServices(
  primary: RecommendationOption,
  output: SystemRecOutput,
  data: CollectedData | null,
): WaterServicesAssessment {
  const input = output.inputEcho;
  const waterType = primary.water || input?.currentWater || 'unknown';
  const strategy = primary.waterLabel || getWaterLabel(waterType);

  const bathrooms = data?.bathrooms || input?.bathrooms || '1';
  const occupancy = data?.occupancyCount || input?.occupancy || '—';

  let reasoning = `Selected for a household of ${occupancy} with ${bathrooms} bathroom${Number(bathrooms) !== 1 ? 's' : ''}.`;
  if (waterType === 'combi_instant' || waterType === 'instant' || waterType === 'on_demand') {
    reasoning += ' Combi avoids cylinder standing losses, but hot water is not truly instant at the tap: expect delivery lag plus some draw-off and burner start/stop energy waste, especially on longer pipe runs.';
  } else if (waterType === 'unvented_cylinder') {
    reasoning += ' Unvented cylinder provides mains-pressure hot water with storage for simultaneous use.';
  } else if (waterType === 'vented_cylinder') {
    reasoning += ' Vented cylinder provides reliable storage-based hot water.';
  }

  const pressure = input?.mainsPressure || input?.mainsStaticPressureBar;
  let mainsLabel = 'Not measured';
  let adequate = true;
  if (typeof pressure === 'number') {
    mainsLabel = `${pressure} bar`;
    if (pressure < 1.0) {
      adequate = false;
      mainsLabel += ' (low — may limit combi/unvented options)';
    }
  }

  const peakPattern = data?.usagePattern || 'standard';
  const peakDemandFit = peakPattern === 'high'
    ? 'High simultaneous demand — storage-based system reduces waiting.'
    : 'Standard demand pattern — system capacity is adequate.';

  return {
    strategy,
    reasoning,
    mainsCapacity: { label: mainsLabel, adequate },
    peakDemandFit,
  };
}
